'use strict'
import React, { useState, useEffect, useMemo, useCallback } from 'react'
import { connect } from 'react-redux'
import axios from 'axios'
import { useParams, useHistory } from 'react-router-dom'
import {
  makeStyles,
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Button,
  CircularProgress,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  Drawer,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon
} from '@material-ui/core'
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab'
import { KeyboardDatePicker } from '@material-ui/pickers'
import {
  NavigateBefore,
  NavigateNext,
  Today,
  ViewDay,
  ViewWeek,
  DateRange,
  Refresh,
  ArrowBack,
  Sync,
  Close,
  Place,
  Schedule,
  AttachMoney,
  TrendingUp,
  TrendingDown,
  DirectionsCar,
  EventNote,
  Delete,
  SwapHoriz,
  History
} from '@material-ui/icons'
import { format, addDays, subDays, startOfWeek, endOfWeek, startOfMonth, endOfMonth, isSameDay } from 'date-fns'
import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
  TimelineMarkers,
  TodayMarker,
  CursorMarker
} from 'react-calendar-timeline'
import moment from 'moment'
import 'react-calendar-timeline/style.css'

const useStyles = makeStyles(theme => ({
  root: {
    width: '100%',
    height: '100vh',
    display: 'flex',
    flexDirection: 'column',
    backgroundColor: theme.palette.grey[100]
  },
  header: {
    padding: theme.spacing(2),
    backgroundColor: '#fff',
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    flexWrap: 'wrap'
  },
  backButton: {
    marginRight: theme.spacing(1)
  },
  title: {
    fontWeight: 600,
    marginRight: theme.spacing(2)
  },
  crewSelect: {
    minWidth: 220
  },
  datePicker: {
    width: 160
  },
  viewToggle: {
    marginLeft: 'auto'
  },
  statsBar: {
    display: 'flex',
    gap: theme.spacing(3),
    padding: theme.spacing(1.5, 2),
    backgroundColor: '#fff',
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  statItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1)
  },
  statValue: {
    fontWeight: 600,
    fontSize: '1.1rem',
    color: theme.palette.primary.main
  },
  statLabel: {
    fontSize: '0.8rem',
    color: theme.palette.text.secondary
  },
  otWarning: {
    color: theme.palette.warning.main
  },
  timelineContainer: {
    flex: 1,
    overflow: 'hidden',
    backgroundColor: '#fff'
  },
  loading: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200
  },
  noData: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: 200,
    flexDirection: 'column',
    gap: theme.spacing(2)
  },
  // Timeline item styles
  stopItem: {
    borderRadius: 6,
    padding: '0 8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.9
    }
  },
  driveItem: {
    borderRadius: 0,
    padding: '0 4px',
    fontSize: '0.75rem',
    opacity: 0.7
  },
  legendBar: {
    display: 'flex',
    gap: theme.spacing(3),
    padding: theme.spacing(1, 2),
    backgroundColor: '#fafafa',
    borderTop: `1px solid ${theme.palette.divider}`
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: '0.75rem'
  },
  legendColor: {
    width: 16,
    height: 16,
    borderRadius: 4
  },
  drawer: {
    width: 400
  },
  drawerHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.main,
    color: '#fff'
  },
  drawerContent: {
    padding: theme.spacing(2)
  },
  drawerSection: {
    marginBottom: theme.spacing(2)
  },
  drawerSectionTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    color: theme.palette.text.secondary,
    fontSize: '0.75rem',
    textTransform: 'uppercase'
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`
  },
  statLabel: {
    color: theme.palette.text.secondary
  },
  statValue: {
    fontWeight: 600
  },
  actionButton: {
    marginTop: theme.spacing(1),
    marginRight: theme.spacing(1)
  },
  profitPositive: {
    color: theme.palette.success?.main || '#4caf50'
  },
  profitNegative: {
    color: theme.palette.error.main
  }
}))

// Colors for timeline items
const COLORS = {
  stop: '#4CAF50',
  drive: '#90CAF9',
  yardDrive: '#B0BEC5',
  ot: '#FF9800'
}

// Format duration in minutes to readable string
const formatDuration = (minutes) => {
  if (!minutes || minutes === 0) return '0m'
  const hours = Math.floor(minutes / 60)
  const mins = Math.round(minutes % 60)
  if (hours === 0) return `${mins}m`
  if (mins === 0) return `${hours}h`
  return `${hours}h ${mins}m`
}

function RoutingCrewSchedule({ url }) {
  const classes = useStyles()
  const history = useHistory()
  const { crewLeaderId: paramCrewId, date: paramDate } = useParams()

  // State
  const [selectedDate, setSelectedDate] = useState(() => {
    if (paramDate) {
      const parsed = new Date(paramDate)
      return isNaN(parsed.getTime()) ? new Date() : parsed
    }
    return new Date()
  })
  const [selectedCrewId, setSelectedCrewId] = useState(paramCrewId ? parseInt(paramCrewId) : null)
  const [viewMode, setViewMode] = useState('day') // 'day', 'week', 'month'
  const [dailyRoutes, setDailyRoutes] = useState([])
  const [allCrewLeaders, setAllCrewLeaders] = useState([])
  const [isLoading, setIsLoading] = useState(false)
  const [isRecalculating, setIsRecalculating] = useState(false)
  const [error, setError] = useState(null)

  // Detail drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [selectedItem, setSelectedItem] = useState(null)
  const [propertyDetails, setPropertyDetails] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(false)

  // Calculate date range based on view mode
  const dateRange = useMemo(() => {
    switch (viewMode) {
      case 'week':
        return {
          start: startOfWeek(selectedDate, { weekStartsOn: 1 }), // Monday
          end: endOfWeek(selectedDate, { weekStartsOn: 1 })
        }
      case 'month':
        return {
          start: startOfMonth(selectedDate),
          end: endOfMonth(selectedDate)
        }
      default: // day
        return {
          start: selectedDate,
          end: selectedDate
        }
    }
  }, [selectedDate, viewMode])

  // Fetch daily routes
  useEffect(() => {
    const fetchRoutes = async () => {
      setIsLoading(true)
      setError(null)

      try {
        const params = {
          startDate: format(dateRange.start, 'yyyy-MM-dd'),
          endDate: format(dateRange.end, 'yyyy-MM-dd')
        }
        if (selectedCrewId) {
          params.crewLeaderId = selectedCrewId
        }

        const response = await axios.get(`${url}/api/aspire/routing/daily-routes`, { params })

        if (response.data?.success) {
          setDailyRoutes(response.data.routes || [])

          // Extract unique crew leaders
          const crewMap = new Map()
          response.data.routes?.forEach(route => {
            if (route.crewLeaderId && route.crewLeaderName) {
              crewMap.set(route.crewLeaderId, route.crewLeaderName)
            }
          })

          // Merge with existing crew leaders (from all routes, not just filtered)
          if (!selectedCrewId) {
            setAllCrewLeaders(Array.from(crewMap, ([id, name]) => ({ id, name })))
          }
        }
      } catch (err) {
        console.error('Error fetching routes:', err)
        setError(err.message)
      } finally {
        setIsLoading(false)
      }
    }

    fetchRoutes()
  }, [url, dateRange, selectedCrewId])

  // Recalculate routes function
  const handleRecalculateRoutes = async () => {
    setIsRecalculating(true)
    try {
      await axios.post(`${url}/api/aspire/routing/recalculate-routes`, { weeksAhead: 8 })
      // Refetch routes after recalculation
      const params = {
        startDate: format(dateRange.start, 'yyyy-MM-dd'),
        endDate: format(dateRange.end, 'yyyy-MM-dd')
      }
      if (selectedCrewId) {
        params.crewLeaderId = selectedCrewId
      }
      const response = await axios.get(`${url}/api/aspire/routing/daily-routes`, { params })
      if (response.data?.success) {
        setDailyRoutes(response.data.routes || [])
      }
    } catch (err) {
      console.error('Error recalculating routes:', err)
      setError('Failed to recalculate routes')
    } finally {
      setIsRecalculating(false)
    }
  }

  // Handle timeline item click - open detail drawer
  const handleItemClick = async (itemId, e, time) => {
    const item = timelineItems.find(i => i.id === itemId)
    if (!item) return

    setSelectedItem(item)
    setDrawerOpen(true)
    setPropertyDetails(null)

    // If it's a stop, fetch property details
    if (item.itemType === 'stop' && item.stopData?.propertyId) {
      setLoadingDetails(true)
      try {
        // Fetch full property details from the routing properties endpoint
        const response = await axios.get(`${url}/api/aspire/routing/properties`)
        const property = response.data?.find(p => p.propertyId === item.stopData.propertyId)
        if (property) {
          setPropertyDetails(property)
        }
      } catch (err) {
        console.error('Error fetching property details:', err)
      } finally {
        setLoadingDetails(false)
      }
    }
  }

  // Close drawer
  const handleCloseDrawer = () => {
    setDrawerOpen(false)
    setSelectedItem(null)
    setPropertyDetails(null)
  }

  // Fetch all crew leaders on mount (for dropdown)
  useEffect(() => {
    const fetchAllCrews = async () => {
      try {
        const crewMap = new Map()

        // First try to get from daily routes
        const today = new Date()
        const params = {
          startDate: format(today, 'yyyy-MM-dd'),
          endDate: format(addDays(today, 60), 'yyyy-MM-dd')
        }
        const routesResponse = await axios.get(`${url}/api/aspire/routing/daily-routes`, { params })

        if (routesResponse.data?.success) {
          routesResponse.data.routes?.forEach(route => {
            if (route.crewLeaderId && route.crewLeaderName) {
              crewMap.set(route.crewLeaderId, route.crewLeaderName)
            }
          })
        }

        // Also fetch from properties to get all crew leaders
        const propertiesResponse = await axios.get(`${url}/api/aspire/routing/properties`)
        let hasUnassigned = false
        if (propertiesResponse.data) {
          propertiesResponse.data.forEach(prop => {
            // From assignedCrewLeaders
            prop.assignedCrewLeaders?.forEach(crew => {
              if (crew.id && crew.name) {
                crewMap.set(crew.id, crew.name)
              }
            })
            // From futureVisits
            prop.futureVisits?.forEach(visit => {
              if (visit.crewLeaderId && visit.crewLeaderName) {
                crewMap.set(visit.crewLeaderId, visit.crewLeaderName)
              } else if (!visit.crewLeaderId) {
                hasUnassigned = true
              }
            })
          })
        }

        const crews = Array.from(crewMap, ([id, name]) => ({ id, name }))
        crews.sort((a, b) => a.name.localeCompare(b.name))
        if (hasUnassigned) {
          crews.push({ id: 'unassigned', name: 'Unassigned' })
        }
        setAllCrewLeaders(crews)
      } catch (err) {
        console.error('Error fetching crews:', err)
      }
    }

    fetchAllCrews()
  }, [url])

  // Get selected crew's routes
  const selectedCrewRoutes = useMemo(() => {
    if (!selectedCrewId) return dailyRoutes
    return dailyRoutes.filter(r => r.crewLeaderId === selectedCrewId)
  }, [dailyRoutes, selectedCrewId])

  // Calculate aggregate stats
  const aggregateStats = useMemo(() => {
    return selectedCrewRoutes.reduce((acc, route) => ({
      totalStops: acc.totalStops + (route.totalStops || 0),
      totalServiceTime: acc.totalServiceTime + (route.totalServiceTime || 0),
      totalDriveTime: acc.totalDriveTime + (route.totalDriveTime || 0),
      totalMiles: acc.totalMiles + (route.totalMiles || 0),
      totalHours: acc.totalHours + (route.totalHours || 0),
      estimatedOTHours: acc.estimatedOTHours + (route.estimatedOTHours || 0),
      daysWithRoutes: acc.daysWithRoutes + 1
    }), {
      totalStops: 0,
      totalServiceTime: 0,
      totalDriveTime: 0,
      totalMiles: 0,
      totalHours: 0,
      estimatedOTHours: 0,
      daysWithRoutes: 0
    })
  }, [selectedCrewRoutes])

  // Build timeline groups (one row per day)
  const timelineGroups = useMemo(() => {
    const groups = []
    let currentDate = new Date(dateRange.start)

    while (currentDate <= dateRange.end) {
      const dateStr = format(currentDate, 'yyyy-MM-dd')
      const route = selectedCrewRoutes.find(r => {
        const routeDate = new Date(r.date)
        return isSameDay(routeDate, currentDate)
      })

      groups.push({
        id: dateStr,
        title: format(currentDate, 'EEE, MMM d'),
        route: route || null,
        date: new Date(currentDate)
      })

      currentDate = addDays(currentDate, 1)
    }

    return groups
  }, [dateRange, selectedCrewRoutes])

  // Build timeline items (stops and drive segments)
  const timelineItems = useMemo(() => {
    const items = []
    let itemId = 0

    // Use a reference day for all time calculations so items appear in the same time window
    // This way each row (day) shows items at the correct time-of-day position
    const referenceDay = moment(dateRange.start).startOf('day')

    timelineGroups.forEach(group => {
      if (!group.route?.stops?.length) return

      // Use reference day for time positioning (so all days show in same time window)
      const dayStart = referenceDay

      // Parse time helper
      const parseTime = (timeStr) => {
        if (!timeStr) return null
        const match = timeStr.match(/(\d+):(\d+)\s*(AM|PM)/i)
        if (!match) return null
        let hours = parseInt(match[1])
        const minutes = parseInt(match[2])
        const isPM = match[3].toUpperCase() === 'PM'
        if (isPM && hours !== 12) hours += 12
        if (!isPM && hours === 12) hours = 0
        return dayStart.clone().add(hours, 'hours').add(minutes, 'minutes')
      }

      let previousDeparture = null

      group.route.stops.forEach((stop, index) => {
        const arrival = parseTime(stop.arrivalTime)
        const departure = parseTime(stop.departureTime)

        if (!arrival || !departure) return

        // Add drive segment before stop
        // Use previous stop's departure as drive start (no gaps)
        // For first stop, calculate backwards from arrival (or use arrival if 0 drive time)
        const driveStart = previousDeparture || (stop.driveTimeMinutes > 0
          ? arrival.clone().subtract(stop.driveTimeMinutes, 'minutes')
          : arrival.clone().subtract(5, 'minutes')) // Show minimal segment for first stop

        // Always add drive segment if there's a gap or drive time
        // This ensures no visual gaps between stops
        const hasDriveGap = previousDeparture && previousDeparture.valueOf() < arrival.valueOf()
        const hasFirstStopDrive = !previousDeparture && index === 0

        if (hasDriveGap || hasFirstStopDrive) {
          items.push({
            id: `drive-${itemId++}`,
            group: group.id,
            title: stop.driveTimeMinutes > 0
              ? `${stop.driveTimeMinutes}m / ${stop.driveMiles?.toFixed(1)}mi`
              : `${stop.driveMiles?.toFixed(1) || 0}mi`,
            start_time: driveStart.valueOf(),
            end_time: arrival.valueOf(),
            itemType: 'drive',
            isYardDrive: stop.isYardDeparture,
            color: stop.isYardDeparture ? COLORS.yardDrive : COLORS.drive,
            canMove: false,
            canResize: false
          })
        }

        // Add stop item
        items.push({
          id: `stop-${itemId++}`,
          group: group.id,
          title: stop.name || `Stop ${stop.order}`,
          start_time: arrival.valueOf(),
          end_time: departure.valueOf(),
          itemType: 'stop',
          stopData: stop,
          color: COLORS.stop,
          estimatedHours: stop.estimatedHours,
          address: stop.address1,
          canMove: false,
          canResize: false
        })

        // Track this stop's departure for next drive segment
        previousDeparture = departure
      })

      // Add return to yard drive if we have the data
      const lastStop = group.route.stops[group.route.stops.length - 1]
      const returnToYard = group.route.returnToYard
      if (lastStop && previousDeparture) {
        // Use actual return-to-yard data if available, otherwise estimate
        const returnDriveTime = returnToYard?.driveTimeMinutes ||
          Math.round(group.route.totalDriveTime / (group.route.stops.length + 1)) || 15
        const returnDriveMiles = returnToYard?.driveMiles || 0

        items.push({
          id: `drive-${itemId++}`,
          group: group.id,
          title: returnDriveTime > 0
            ? `${returnDriveTime}m / ${returnDriveMiles.toFixed(1)}mi`
            : `${returnDriveMiles.toFixed(1)}mi`,
          start_time: previousDeparture.valueOf(),
          end_time: previousDeparture.valueOf() + (returnDriveTime * 60 * 1000),
          itemType: 'drive',
          isYardReturn: true,
          color: COLORS.yardDrive,
          canMove: false,
          canResize: false,
          returnToYardData: returnToYard
        })
      }
    })

    return items
  }, [timelineGroups, dateRange])

  // Timeline bounds
  const timelineBounds = useMemo(() => {
    const dayStart = moment(selectedDate).startOf('day').hour(5) // 5 AM
    const dayEnd = moment(selectedDate).startOf('day').hour(20) // 8 PM
    return {
      start: dayStart.valueOf(),
      end: dayEnd.valueOf()
    }
  }, [selectedDate])

  // Navigation handlers
  const handlePrevious = () => {
    switch (viewMode) {
      case 'week':
        setSelectedDate(subDays(selectedDate, 7))
        break
      case 'month':
        setSelectedDate(subDays(startOfMonth(selectedDate), 1))
        break
      default:
        setSelectedDate(subDays(selectedDate, 1))
    }
  }

  const handleNext = () => {
    switch (viewMode) {
      case 'week':
        setSelectedDate(addDays(selectedDate, 7))
        break
      case 'month':
        setSelectedDate(addDays(endOfMonth(selectedDate), 1))
        break
      default:
        setSelectedDate(addDays(selectedDate, 1))
    }
  }

  const handleToday = () => {
    setSelectedDate(new Date())
  }

  // Update URL when crew/date changes
  useEffect(() => {
    const dateStr = format(selectedDate, 'yyyy-MM-dd')
    const newPath = selectedCrewId
      ? `/routing/schedule/${selectedCrewId}/${dateStr}`
      : `/routing/schedule`

    if (history.location.pathname !== newPath) {
      history.replace(newPath)
    }
  }, [selectedCrewId, selectedDate, history])

  // Custom item renderer
  const itemRenderer = ({ item, itemContext, getItemProps }) => {
    const isStop = item.itemType === 'stop'
    const isDrive = item.itemType === 'drive'

    const style = {
      backgroundColor: item.color,
      color: isStop ? '#fff' : '#333',
      borderRadius: isStop ? 6 : 2,
      border: 'none',
      padding: '0 8px',
      fontSize: isStop ? '0.85rem' : '0.7rem',
      fontWeight: isStop ? 600 : 400,
      height: isStop ? 36 : 20,
      lineHeight: isStop ? '36px' : '20px',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      overflow: 'hidden',
      boxShadow: isStop ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
      opacity: item.isYardDrive || item.isYardReturn ? 0.7 : 1
    }

    const itemProps = getItemProps({ style })
    delete itemProps.title // Remove default title to avoid tooltip

    const content = (
      <div {...itemProps}>
        <span style={{
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          flex: 1
        }}>
          {item.title}
        </span>
        {isStop && item.estimatedHours > 0 && (
          <Chip
            size="small"
            label={`${item.estimatedHours.toFixed(1)}h`}
            style={{
              backgroundColor: 'rgba(255,255,255,0.3)',
              height: 18,
              marginLeft: 4,
              fontSize: '0.7rem'
            }}
          />
        )}
      </div>
    )

    if (isStop) {
      return (
        <Tooltip
          title={
            <div>
              <div style={{ fontWeight: 'bold' }}>{item.title}</div>
              {item.address && <div style={{ marginTop: 4 }}>{item.address}</div>}
              <div style={{ marginTop: 4 }}>Duration: {item.estimatedHours?.toFixed(1) || 1}h</div>
            </div>
          }
        >
          {content}
        </Tooltip>
      )
    }

    return content
  }

  // Custom group renderer (day sidebar)
  const groupRenderer = ({ group }) => {
    const route = group.route
    const hasRoute = !!route

    // Calculate start/end times from stops
    let startTime = null
    let endTime = null
    if (route?.stops?.length > 0) {
      const firstStop = route.stops[0]
      const lastStop = route.stops[route.stops.length - 1]

      // Start time: first stop arrival minus drive time (when they leave yard)
      if (firstStop.arrivalTime) {
        const match = firstStop.arrivalTime.match(/(\d+):(\d+)\s*(AM|PM)/i)
        if (match) {
          let hours = parseInt(match[1])
          const minutes = parseInt(match[2])
          const isPM = match[3].toUpperCase() === 'PM'
          if (isPM && hours !== 12) hours += 12
          if (!isPM && hours === 12) hours = 0
          // Subtract drive time to yard departure
          const totalMinutes = hours * 60 + minutes - (firstStop.driveTimeMinutes || 0)
          const startHours = Math.floor(totalMinutes / 60)
          const startMins = totalMinutes % 60
          const startAmPm = startHours >= 12 ? 'PM' : 'AM'
          const displayHours = startHours % 12 || 12
          startTime = `${displayHours}:${startMins.toString().padStart(2, '0')} ${startAmPm}`
        }
      }

      // End time: last stop departure plus estimated return drive
      if (lastStop.departureTime) {
        const match = lastStop.departureTime.match(/(\d+):(\d+)\s*(AM|PM)/i)
        if (match) {
          let hours = parseInt(match[1])
          const minutes = parseInt(match[2])
          const isPM = match[3].toUpperCase() === 'PM'
          if (isPM && hours !== 12) hours += 12
          if (!isPM && hours === 12) hours = 0
          // Add estimated return drive (use avg or 20 min default)
          const returnDrive = route.totalDriveTime ? Math.round(route.totalDriveTime / (route.stops.length + 1)) : 20
          const totalMinutes = hours * 60 + minutes + returnDrive
          const endHours = Math.floor(totalMinutes / 60)
          const endMins = totalMinutes % 60
          const endAmPm = endHours >= 12 ? 'PM' : 'AM'
          const displayHours = endHours % 12 || 12
          endTime = `${displayHours}:${endMins.toString().padStart(2, '0')} ${endAmPm}`
        }
      }
    }

    return (
      <div style={{
        padding: '8px 12px',
        borderLeft: `4px solid ${hasRoute ? COLORS.stop : '#ccc'}`,
        backgroundColor: isSameDay(group.date, new Date()) ? '#fff9c4' : '#fff',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center'
      }}>
        <Typography variant="subtitle2" style={{ fontWeight: 600 }}>
          {group.title}
        </Typography>
        {hasRoute && (
          <>
            <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
              <Chip size="small" label={`${route.totalStops} stops`} variant="outlined" />
              <Chip
                size="small"
                label={`${route.totalHours?.toFixed(1)}h`}
                color={route.estimatedOTHours > 0 ? 'secondary' : 'primary'}
                variant="outlined"
              />
            </div>
            {startTime && endTime && (
              <Typography variant="caption" style={{ marginTop: 4, color: '#666' }}>
                {startTime} → {endTime}
              </Typography>
            )}
          </>
        )}
      </div>
    )
  }

  const selectedCrewName = allCrewLeaders.find(c => c.id === selectedCrewId)?.name || 'All Crews'

  return (
    <div className={classes.root}>
      {/* Header */}
      <div className={classes.header}>
        <IconButton
          className={classes.backButton}
          onClick={() => history.push('/routing')}
          size="small"
        >
          <ArrowBack />
        </IconButton>

        <Typography variant="h5" className={classes.title}>
          Crew Schedule
        </Typography>

        <FormControl variant="outlined" size="small" className={classes.crewSelect}>
          <InputLabel>Crew Leader</InputLabel>
          <Select
            value={selectedCrewId || ''}
            onChange={(e) => setSelectedCrewId(e.target.value || null)}
            label="Crew Leader"
          >
            <MenuItem value="">All Crews</MenuItem>
            {allCrewLeaders.map(crew => (
              <MenuItem key={crew.id} value={crew.id}>{crew.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Box display="flex" alignItems="center" gap={1}>
          <IconButton size="small" onClick={handlePrevious}>
            <NavigateBefore />
          </IconButton>

          <KeyboardDatePicker
            className={classes.datePicker}
            value={selectedDate}
            onChange={setSelectedDate}
            format="MM/dd/yyyy"
            inputVariant="outlined"
            size="small"
            autoOk
          />

          <IconButton size="small" onClick={handleNext}>
            <NavigateNext />
          </IconButton>

          <IconButton size="small" onClick={handleToday} color="primary">
            <Today />
          </IconButton>
        </Box>

        <ToggleButtonGroup
          value={viewMode}
          exclusive
          onChange={(e, value) => value && setViewMode(value)}
          size="small"
          className={classes.viewToggle}
        >
          <ToggleButton value="day">
            <ViewDay style={{ marginRight: 4 }} /> Day
          </ToggleButton>
          <ToggleButton value="week">
            <ViewWeek style={{ marginRight: 4 }} /> Week
          </ToggleButton>
          <ToggleButton value="month">
            <DateRange style={{ marginRight: 4 }} /> Month
          </ToggleButton>
        </ToggleButtonGroup>

        <Tooltip title="Refresh page">
          <IconButton size="small" onClick={() => window.location.reload()}>
            <Refresh />
          </IconButton>
        </Tooltip>

        <Tooltip title="Recalculate all routes (may take a few minutes)">
          <span>
            <Button
              variant="outlined"
              size="small"
              startIcon={isRecalculating ? <CircularProgress size={16} /> : <Sync />}
              onClick={handleRecalculateRoutes}
              disabled={isRecalculating}
            >
              {isRecalculating ? 'Calculating...' : 'Recalculate Routes'}
            </Button>
          </span>
        </Tooltip>
      </div>

      {/* Stats Bar */}
      <div className={classes.statsBar}>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{aggregateStats.totalStops}</span>
          <span className={classes.statLabel}>Stops</span>
        </div>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{formatDuration(aggregateStats.totalServiceTime)}</span>
          <span className={classes.statLabel}>Service Time</span>
        </div>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{formatDuration(aggregateStats.totalDriveTime)}</span>
          <span className={classes.statLabel}>Drive Time</span>
        </div>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{aggregateStats.totalMiles.toFixed(1)} mi</span>
          <span className={classes.statLabel}>Total Miles</span>
        </div>
        <div className={classes.statItem}>
          <span className={classes.statValue}>{aggregateStats.totalHours.toFixed(1)}h</span>
          <span className={classes.statLabel}>Total Hours</span>
        </div>
        {aggregateStats.estimatedOTHours > 0 && (
          <div className={classes.statItem}>
            <span className={`${classes.statValue} ${classes.otWarning}`}>
              +{aggregateStats.estimatedOTHours.toFixed(1)}h
            </span>
            <span className={classes.statLabel}>Est. OT</span>
          </div>
        )}
        <div className={classes.statItem} style={{ marginLeft: 'auto' }}>
          <span className={classes.statLabel}>
            {viewMode === 'day'
              ? format(selectedDate, 'EEEE, MMMM d, yyyy')
              : viewMode === 'week'
                ? `${format(dateRange.start, 'MMM d')} - ${format(dateRange.end, 'MMM d, yyyy')}`
                : format(selectedDate, 'MMMM yyyy')
            }
          </span>
        </div>
      </div>

      {/* Timeline */}
      <div className={classes.timelineContainer}>
        {isLoading ? (
          <div className={classes.loading}>
            <CircularProgress />
          </div>
        ) : timelineGroups.length === 0 || (selectedCrewId && selectedCrewRoutes.length === 0) ? (
          <div className={classes.noData}>
            <Typography variant="h6" color="textSecondary">
              No routes scheduled
            </Typography>
            <Typography variant="body2" color="textSecondary">
              {selectedCrewId
                ? `${selectedCrewName} has no routes for this ${viewMode}`
                : `No routes found for this ${viewMode}`
              }
            </Typography>
          </div>
        ) : (
          <Timeline
            groups={timelineGroups}
            items={timelineItems}
            defaultTimeStart={moment(dateRange.start).startOf('day').hour(5)}
            defaultTimeEnd={moment(dateRange.start).startOf('day').hour(20)}
            lineHeight={viewMode === 'day' ? 80 : 60}
            itemHeightRatio={0.8}
            sidebarWidth={180}
            canMove={false}
            canResize={false}
            stackItems
            itemRenderer={itemRenderer}
            groupRenderer={groupRenderer}
            onItemClick={handleItemClick}
          >
            <TimelineHeaders>
              <SidebarHeader>
                {({ getRootProps }) => (
                  <div {...getRootProps()} style={{ padding: 8 }}>
                    <Typography variant="subtitle2">
                      {selectedCrewName}
                    </Typography>
                  </div>
                )}
              </SidebarHeader>
              <DateHeader unit="primaryHeader" />
              <DateHeader />
            </TimelineHeaders>
            <TimelineMarkers>
              <TodayMarker />
              <CursorMarker>
                {({ styles, date }) => (
                  <div style={{ ...styles, backgroundColor: 'rgba(0,0,0,0.5)', width: 1 }}>
                    <div style={{
                      position: 'absolute',
                      top: -20,
                      left: 4,
                      backgroundColor: 'rgba(0,0,0,0.75)',
                      color: '#fff',
                      padding: '2px 6px',
                      borderRadius: 3,
                      fontSize: 11,
                      whiteSpace: 'nowrap'
                    }}>
                      {moment(date).format('h:mm A')}
                    </div>
                  </div>
                )}
              </CursorMarker>
            </TimelineMarkers>
          </Timeline>
        )}
      </div>

      {/* Legend */}
      <div className={classes.legendBar}>
        <div className={classes.legendItem}>
          <div className={classes.legendColor} style={{ backgroundColor: COLORS.stop }} />
          <span>On-Site</span>
        </div>
        <div className={classes.legendItem}>
          <div className={classes.legendColor} style={{ backgroundColor: COLORS.drive }} />
          <span>Drive Time</span>
        </div>
        <div className={classes.legendItem}>
          <div className={classes.legendColor} style={{ backgroundColor: COLORS.yardDrive }} />
          <span>Yard Travel</span>
        </div>
      </div>

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
      >
        <div className={classes.drawer}>
          {/* Drawer Header */}
          <div className={classes.drawerHeader} style={{
            backgroundColor: selectedItem?.itemType === 'stop' ? COLORS.stop : COLORS.drive
          }}>
            <div>
              <Typography variant="h6">
                {selectedItem?.itemType === 'stop' ? selectedItem?.title : 'Drive Segment'}
              </Typography>
              {selectedItem?.itemType === 'stop' && selectedItem?.stopData?.address1 && (
                <Typography variant="body2" style={{ opacity: 0.9 }}>
                  {selectedItem.stopData.address1}
                </Typography>
              )}
            </div>
            <IconButton size="small" onClick={handleCloseDrawer} style={{ color: '#fff' }}>
              <Close />
            </IconButton>
          </div>

          <div className={classes.drawerContent}>
            {selectedItem?.itemType === 'drive' ? (
              // Drive segment details
              <div className={classes.drawerSection}>
                <Typography className={classes.drawerSectionTitle}>Drive Details</Typography>
                <div className={classes.statRow}>
                  <span className={classes.statLabel}>Drive Info</span>
                  <span className={classes.statValue}>{selectedItem?.title}</span>
                </div>
                <div className={classes.statRow}>
                  <span className={classes.statLabel}>Type</span>
                  <span className={classes.statValue}>
                    {selectedItem?.isYardDrive ? 'From Yard' : selectedItem?.isYardReturn ? 'Return to Yard' : 'Between Stops'}
                  </span>
                </div>
                {selectedItem?.isYardReturn && selectedItem?.returnToYardData && (
                  <div className={classes.statRow}>
                    <span className={classes.statLabel}>Est. Return Time</span>
                    <span className={classes.statValue}>
                      {selectedItem.returnToYardData.estimatedReturnTime || '-'}
                    </span>
                  </div>
                )}
                <Typography variant="caption" color="textSecondary" style={{ marginTop: 12, display: 'block' }}>
                  Note: Drive segment width shows the time gap between stops, which may include breaks or waiting time beyond just driving.
                </Typography>
              </div>
            ) : (
              // Property/Stop details
              <>
                {/* Schedule Info */}
                <div className={classes.drawerSection}>
                  <Typography className={classes.drawerSectionTitle}>Schedule</Typography>
                  <div className={classes.statRow}>
                    <span className={classes.statLabel}>Arrival</span>
                    <span className={classes.statValue}>{selectedItem?.stopData?.arrivalTime || '-'}</span>
                  </div>
                  <div className={classes.statRow}>
                    <span className={classes.statLabel}>Departure</span>
                    <span className={classes.statValue}>{selectedItem?.stopData?.departureTime || '-'}</span>
                  </div>
                  <div className={classes.statRow}>
                    <span className={classes.statLabel}>Est. Service Time</span>
                    <span className={classes.statValue}>{selectedItem?.stopData?.estimatedHours?.toFixed(1) || '-'} hrs</span>
                  </div>
                  <div className={classes.statRow}>
                    <span className={classes.statLabel}>Drive To</span>
                    <span className={classes.statValue}>
                      {selectedItem?.stopData?.driveTimeMinutes || 0}m / {selectedItem?.stopData?.driveMiles?.toFixed(1) || 0}mi
                    </span>
                  </div>
                </div>

                {/* Property Details (from full data) */}
                {loadingDetails ? (
                  <div style={{ textAlign: 'center', padding: 20 }}>
                    <CircularProgress size={24} />
                    <Typography variant="body2" color="textSecondary">Loading property details...</Typography>
                  </div>
                ) : propertyDetails ? (
                  <>
                    <Divider style={{ margin: '16px 0' }} />

                    {/* Visit History */}
                    <div className={classes.drawerSection}>
                      <Typography className={classes.drawerSectionTitle}>Visit History</Typography>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Completed Visits</span>
                        <span className={classes.statValue}>{propertyDetails.completedOccurrences || 0}</span>
                      </div>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Annual Visits</span>
                        <span className={classes.statValue}>{propertyDetails.annualOccurrences || 0}</span>
                      </div>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Future Visits</span>
                        <span className={classes.statValue}>{propertyDetails.futureVisits?.length || 0}</span>
                      </div>
                    </div>

                    {/* Hours Analysis */}
                    <div className={classes.drawerSection}>
                      <Typography className={classes.drawerSectionTitle}>Hours Analysis</Typography>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Est. Hours/Visit</span>
                        <span className={classes.statValue}>{propertyDetails.estimatedHours?.toFixed(1) || '-'}</span>
                      </div>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Avg Actual Hours</span>
                        <span className={classes.statValue}>{propertyDetails.avgActualHours?.toFixed(1) || '-'}</span>
                      </div>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Hour Variance</span>
                        <span className={classes.statValue} style={{
                          color: (propertyDetails.estimationVariance || 0) < 0 ? '#f44336' : '#4caf50'
                        }}>
                          {propertyDetails.estimationVariance?.toFixed(1) || '-'} hrs
                        </span>
                      </div>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Total OT Hours</span>
                        <span className={classes.statValue} style={{
                          color: (propertyDetails.totalOTHours || 0) > 0 ? '#ff9800' : 'inherit'
                        }}>
                          {propertyDetails.totalOTHours?.toFixed(1) || '0'}
                        </span>
                      </div>
                    </div>

                    {/* Financial */}
                    <div className={classes.drawerSection}>
                      <Typography className={classes.drawerSectionTitle}>Financials</Typography>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Revenue</span>
                        <span className={classes.statValue}>
                          ${(propertyDetails.totalRevenue || 0).toLocaleString()}
                        </span>
                      </div>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Labor Cost</span>
                        <span className={classes.statValue}>
                          ${(propertyDetails.totalLaborCost || 0).toLocaleString()}
                        </span>
                      </div>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Net Profit</span>
                        <span className={classes.statValue} style={{
                          color: (propertyDetails.netProfit || 0) < 0 ? '#f44336' : '#4caf50'
                        }}>
                          ${(propertyDetails.netProfit || 0).toLocaleString()}
                        </span>
                      </div>
                      <div className={classes.statRow}>
                        <span className={classes.statLabel}>Margin</span>
                        <span className={classes.statValue} style={{
                          color: (propertyDetails.profitMargin || 0) < 0 ? '#f44336' : '#4caf50'
                        }}>
                          {propertyDetails.profitMargin?.toFixed(1) || '-'}%
                        </span>
                      </div>
                    </div>

                    {/* Scheduled Days */}
                    {propertyDetails.scheduledDays?.length > 0 && (
                      <div className={classes.drawerSection}>
                        <Typography className={classes.drawerSectionTitle}>Scheduled Days</Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                          {propertyDetails.scheduledDays.map(day => (
                            <Chip key={day} label={day} size="small" variant="outlined" />
                          ))}
                        </Box>
                      </div>
                    )}

                    <Divider style={{ margin: '16px 0' }} />

                    {/* Actions */}
                    <div className={classes.drawerSection}>
                      <Typography className={classes.drawerSectionTitle}>Actions</Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<SwapHoriz />}
                        className={classes.actionButton}
                        disabled
                      >
                        Change Day
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<Delete />}
                        className={classes.actionButton}
                        color="secondary"
                        disabled
                      >
                        Remove from Route
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<History />}
                        className={classes.actionButton}
                        disabled
                      >
                        View History
                      </Button>
                      <Typography variant="caption" color="textSecondary" display="block" style={{ marginTop: 8 }}>
                        Route editing coming soon...
                      </Typography>
                    </div>
                  </>
                ) : (
                  <Typography variant="body2" color="textSecondary" style={{ marginTop: 16 }}>
                    No additional property data available.
                  </Typography>
                )}
              </>
            )}
          </div>
        </div>
      </Drawer>
    </div>
  )
}

const mapStateToProps = (state) => ({
  url: state.url
})

export default connect(mapStateToProps)(RoutingCrewSchedule)
